import { Component, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { OTAJobCreationService, JobCreationData, JobCreationFormData, DropdownOption } from 'src/app/Core/services/ota-job-creation.service';
import { semverValidator } from 'src/app/Core/validators/semver.validator';
import { MatDialog } from '@angular/material/dialog';
import { OtaService } from 'src/app/Core/services/ota.service';
import {
  OtaModelSelectionDialogComponent,
  OtaModelSelectionDialogResult,
  SignedMetadataRecord
} from '../ota-model-selection-dialog/ota-model-selection-dialog.component';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { ToasterService } from 'src/app/Core/services/toaster.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-create-ota-job',
  templateUrl: './create-ota-job.component.html',
  styleUrls: ['./create-ota-job.component.css']
})
export class CreateOtaJobComponent implements OnInit {
  currentStep = 1;
  totalSteps = 5;
  private readonly useMockSignedMetadata = false;
  private confirmedActualModel = '';
  private confirmedActualModelResponse: any;
  isSignedMetadataLoading = false;

  get isMainBoardFirmwareDisabled(): boolean {
    return !this.confirmedActualModelResponse?.[0]?.sign_metadata?.mb;
  }

  // Form data
  jobData: JobCreationFormData = {
    job_name: '',
    model: '',
    actual_model: '',
    job_type: '',
    upgrade_type: '',
    region: '',
    inprogress_timeout_in_minutes: 10,
    wifi_firmware_version: '',
    main_firmware_version: '',
    wifi_firmware_path_on_device: 'wifi-mcu',
    main_firmware_path_on_device: 'mb-mcu',
    job_executions_rollout_config: {
      baseRatePerMinute: 50,
      incrementFactor: 2,
      numberOfSucceededThings: 1000,
      maximumPerMinute: 1000
    },
    abort_config: {
      configurations: [
        {
          action: 'CANCEL',
          failureType: 'FAILED',
          minExecutedThings: 100,
          thresholdPercentage: 20
        },
        {
          action: 'CANCEL',
          failureType: 'TIMED_OUT',
          minExecutedThings: 100,
          thresholdPercentage: 20
        }
      ],
    }
  };

  // Store initial data for reset
  initialJobData: JobCreationFormData = JSON.parse(JSON.stringify(this.jobData));

  // Dropdown options
  models: DropdownOption[] = [];
  actualModels: DropdownOption[] = [];
  jobTypes: DropdownOption[] = [];
  upgradeTypes: DropdownOption[] = [];
  regions: DropdownOption[] = [];
  failureTypeOptions: DropdownOption[] = [
    { label: 'FAILED', value: 'FAILED' },
    { label: 'REJECTED', value: 'REJECTED' },
    { label: 'TIMED_OUT', value: 'TIMED_OUT' }
  ];

  // UI State
  isLoading = false;
  errorMessage = '';
  successMessage = '';
  jobCreated = false;
  createdJobId: number | null = null;

  // Review section state
  expandedSections = {
    basicDetails: true,
    wifiBoard: false,
    mainBoard: false,
    configurations: false
  };

  constructor(
    private jobCreationService: OTAJobCreationService,
    private router: Router,
    private dialog: MatDialog,
    private otaService: OtaService,
    private toaster: ToasterService
  ) { }

  ngOnInit(): void {
    this.loadDropdownOptions();
    this.ensureAbortFailureTypeDefaults();
    this.initialJobData = JSON.parse(JSON.stringify(this.jobData));
    this.confirmedActualModel = this.jobData.actual_model;
  }

  /**
   * Load all dropdown options
   */
  loadDropdownOptions(): void {
    this.jobCreationService.getModels().subscribe(data => this.models = data);
    this.jobCreationService.getActualModels().subscribe(data => this.actualModels = data);
    this.jobCreationService.getJobTypes().subscribe(data => this.jobTypes = data);
    this.jobCreationService.getUpgradeTypes().subscribe(data => this.upgradeTypes = data);
    this.jobCreationService.getRegions().subscribe(data => this.regions = data);
  }

  getFailureTypeOptions(configIndex: number): DropdownOption[] {
    const selected = this.jobData.abort_config?.configurations?.[configIndex]?.failureType;
    const otherIndex = configIndex === 0 ? 1 : 0;
    const otherSelected = this.jobData.abort_config?.configurations?.[otherIndex]?.failureType;

    return this.failureTypeOptions.filter((option) => option.value === selected || option.value !== otherSelected);
  }

  onFailureTypeChange(configIndex: number): void {
    if (!this.jobData.abort_config?.configurations?.length) {
      return;
    }

    const configurations = this.jobData.abort_config.configurations;
    const otherIndex = configIndex === 0 ? 1 : 0;

    if (configurations[configIndex]?.failureType === configurations[otherIndex]?.failureType) {
      const fallback = this.failureTypeOptions.find(
        (option) => option.value !== configurations[configIndex].failureType
      )?.value;

      if (fallback) {
        configurations[otherIndex].failureType = fallback;
      }
    }
  }

  private ensureAbortFailureTypeDefaults(): void {
    if (!this.jobData.abort_config?.configurations || this.jobData.abort_config.configurations.length < 2) {
      this.jobData.abort_config = {
        configurations: [
          {
            action: 'CANCEL',
            failureType: 'FAILED',
            minExecutedThings: 100,
            thresholdPercentage: 20
          },
          {
            action: 'CANCEL',
            failureType: 'TIMED_OUT',
            minExecutedThings: 100,
            thresholdPercentage: 20
          }
        ]
      };
      return;
    }

    this.jobData.abort_config.configurations[0].failureType = this.jobData.abort_config.configurations[0].failureType || 'FAILED';
    this.jobData.abort_config.configurations[1].failureType = this.jobData.abort_config.configurations[1].failureType || 'TIMED_OUT';

    if (this.jobData.abort_config.configurations[0].failureType === this.jobData.abort_config.configurations[1].failureType) {
      this.jobData.abort_config.configurations[1].failureType = 'TIMED_OUT';
      if (this.jobData.abort_config.configurations[0].failureType === 'TIMED_OUT') {
        this.jobData.abort_config.configurations[1].failureType = 'FAILED';
      }
    }
  }

  /**
   * Check if current step is valid
   */
  isCurrentStepValid(): boolean {
    switch (this.currentStep) {
      case 1: // Basic Details
        return this.jobData.job_name.trim() !== '' &&
          this.jobData.model !== '' &&
          this.jobData.inprogress_timeout_in_minutes > 0;
      case 2: // Wi-Fi Board Firmware
        return !!this.jobData.wifi_firmware_version &&
          !this.isWifiVersionInvalid() &&
          !!this.jobData.wifi_firmware_path_on_device &&
          !!this.jobData.wifi_firmware_priority &&
          !!this.jobData.wifi_firmware_description;
      case 3: // Main Board Firmware
        {
          const {
            main_firmware_version,
            main_firmware_path_on_device,
            main_firmware_priority,
            main_firmware_description
          } = this.jobData;

          const isAnyFieldFilled =
            !!main_firmware_version ||
            !!main_firmware_priority ||
            !!main_firmware_description;

          // If nothing filled → allow
          if (!isAnyFieldFilled) {
            return true;
          }

          // If something filled → require everything + valid version
          return !!main_firmware_version &&
            !this.isMainboardVersionInvalid() &&
            !!main_firmware_path_on_device &&
            !!main_firmware_priority &&
            !!main_firmware_description;
        }
      case 4: // Configurations
        return true;
      case 5: // Review and Finish
        return true;
      default:
        return false;
    }
  }

  /**
   * Move to next step
   */
  nextStep(): void {
    console.log("confirmedActualModelResponse:", this.confirmedActualModelResponse);
    console.log("confirmedActualModelResponse?.[0]?.sign_metadata:", this.confirmedActualModelResponse?.[0]?.sign_metadata.mb);

    if (this.isCurrentStepValid()) {
      console.log("confirmedActualModel:", this.confirmedActualModel);
      console.log("confirmedActualModelResponse:", this.confirmedActualModelResponse);
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
        // Skip step 3 if Main Board Firmware is disabled
        if (this.currentStep === 3 && this.isMainBoardFirmwareDisabled) {
          this.currentStep = 4;
        }
        this.errorMessage = '';
        window.scrollTo(0, 0);
      } else {
        this.submitJob();
      }
    } else {
      this.errorMessage = 'Please fill all required fields in this step';
    }
  }

  /**
   * Move to previous step
   */
  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      // Skip step 3 if Main Board Firmware is disabled
      if (this.currentStep === 3 && this.isMainBoardFirmwareDisabled) {
        this.currentStep = 2;
      }
      this.errorMessage = '';
      window.scrollTo(0, 0);
    }
  }

  /**
   * Reset current step data
   */
  resetCurrentStep(): void {
    if (confirm('Are you sure you want to reset this step?')) {
      switch (this.currentStep) {
        case 1: // Reset Basic Details
          this.jobData.job_name = '';
          this.jobData.model = '';
          this.jobData.inprogress_timeout_in_minutes = 10;
          this.jobData.actual_model = '';
          this.confirmedActualModel = '';
          this.confirmedActualModelResponse = null;
          break;
        case 2: // Reset Wi-Fi Board Firmware
          this.jobData.wifi_firmware_version = '';
          this.jobData.wifi_firmware_path_on_device = 'wifi-mcu';
          this.jobData.wifi_firmware_priority = '';
          this.jobData.wifi_firmware_description = '';
          break;
        case 3: // Reset Main Board Firmware
          this.jobData.main_firmware_version = '';
          this.jobData.main_firmware_path_on_device = 'mb-mcu';
          this.jobData.main_firmware_priority = '';
          this.jobData.main_firmware_description = '';
          break;
        case 4: // Reset Configurations
          this.jobData.job_executions_rollout_config = {
            baseRatePerMinute: 50,
            incrementFactor: 2,
            numberOfSucceededThings: 1000,
            maximumPerMinute: 1000
          };
          this.jobData.abort_config = {
            configurations: [
              {
                action: 'CANCEL',
                failureType: 'FAILED',
                minExecutedThings: 100,
                thresholdPercentage: 20
              },
              {
                action: 'CANCEL',
                failureType: 'TIMED_OUT',
                minExecutedThings: 100,
                thresholdPercentage: 20
              }
            ],
          };
          this.ensureAbortFailureTypeDefaults();
          break;
      }
      this.errorMessage = '';
    }
  }

  /**
   * Cancel job creation
   */
  cancelCreation(): void {
    if (confirm('Are you sure you want to cancel? All changes will be lost.')) {
      this.router.navigate(['/ota-dashboard']);
    }
  }

  /**
   * Toggle section in review
   */
  // toggleSection(section: keyof typeof this.expandedSections): void {
  //   this.expandedSections[section] = !this.expandedSections[section];
  // }

  toggleSection(sectionName: string): void {
  const sectionKey = sectionName as keyof typeof this.expandedSections;
  if (sectionKey in this.expandedSections) {
    this.expandedSections[sectionKey] = !this.expandedSections[sectionKey];
  }
}

  /**
   * Submit job
   */
  submitJob(): void {
    const validation = this.jobCreationService.validateJobData(this.jobData);

    if (!validation.valid) {
      this.errorMessage = validation.errors.join(', ');
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const createJobPayload = this.buildCreateJobPayload();

    console.log('Creating job with data:', createJobPayload);
    console.log('Signining bin Metadata:', this.confirmedActualModelResponse)

    this.jobCreationService.createJob(createJobPayload).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.jobCreated = true;
        this.createdJobId = response.id;
        this.successMessage = 'Job created successfully!';
        console.log('Job created:', response);
      },
      error: (error) => {
        this.isLoading = false;
        if (error instanceof HttpErrorResponse) {
          this.toaster.openDialogBox(error.error?.message ?? "Failed to create job. Please try again.", false);
        } else {
          this.toaster.openDialogBox(error.message ?? "Failed to create job. Please try again.", false);
        }
      }
    });
  }

  /**
   * Navigate back to dashboard
   */
  goBackToDashboard(): void {
    this.router.navigate(['/ota-dashboard']);
  }

  /**
   * Create another job
   */
  createAnotherJob(): void {
    this.jobCreated = false;
    this.createdJobId = null;
    this.currentStep = 1;
    this.jobData = {
      job_name: '',
      model: '',
      actual_model: '',
      job_type: '',
      upgrade_type: '',
      region: '',
      inprogress_timeout_in_minutes: 10,
      wifi_firmware_version: '',
      wifi_firmware_path_on_device: 'wifi-mcu',
      wifi_firmware_priority: '',
      wifi_firmware_description: '',
      main_firmware_version: '',
      main_firmware_path_on_device: 'mb-mcu',
      main_firmware_priority: '',
      main_firmware_description: '',
      job_executions_rollout_config: {
        baseRatePerMinute: 50,
        incrementFactor: 2,
        numberOfSucceededThings: 1000,
        maximumPerMinute: 1000
      },
      abort_config: {
        configurations: [
          {
            action: 'CANCEL',
            failureType: 'FAILED',
            minExecutedThings: 100,
            thresholdPercentage: 20
          },
          {
            action: 'CANCEL',
            failureType: 'TIMED_OUT',
            minExecutedThings: 100,
            thresholdPercentage: 20
          }
        ]
      }
    };
    this.ensureAbortFailureTypeDefaults();
    this.errorMessage = '';
    this.successMessage = '';
    window.scrollTo(0, 0);
  }

  /**
   * Get step status
   */
  getStepStatus(step: number): string {
    if (step < this.currentStep) return 'completed';
    if (step === this.currentStep) return 'active';
    return 'pending';
  }

  /**
   * Get dropdown label
   */
  getDropdownLabel(value: string, options: DropdownOption[]): string {
    return options.find(opt => opt.value === value)?.label || value;
  }

  /**
   * Get list of visible steps based on whether main board firmware is disabled
   */
  getVisibleSteps(): number[] {
    return this.isMainBoardFirmwareDisabled ? [1, 2, 4, 5] : [1, 2, 3, 4, 5];
  }

  /**
   * Get the display step number for a given internal step number
   */
  getDisplayStepNumber(step: number): number {
    return this.getVisibleSteps().indexOf(step) + 1;
  }

  isWifiVersionInvalid(): boolean {
    if (!this.jobData.wifi_firmware_version) return false;

    const control = { value: this.jobData.wifi_firmware_version } as AbstractControl;
    return !!semverValidator()(control);
  }

  isMainboardVersionInvalid(): boolean {
    if (!this.jobData.main_firmware_version) return false;

    const control = { value: this.jobData.main_firmware_version } as AbstractControl;
    return !!semverValidator()(control); 
  }

  allowOnlyAlphanumeric(event: KeyboardEvent): void {
    const char = event.key;
    const regex = /^[a-zA-Z0-9]+$/;

    if (!regex.test(char)) {
      event.preventDefault();
    }
  }

  sanitizeJobName(value: string): void {
    this.jobData.job_name = (value || '').replace(/[^a-zA-Z0-9]/g, '');
  }

  private clearMainBoardFirmwareDetails(): void {
    this.jobData.main_firmware_version = '';
    this.jobData.main_firmware_path_on_device = 'mb-mcu';
    this.jobData.main_firmware_priority = '';
    this.jobData.main_firmware_description = '';
  }

  onActualModelChange(value: string): void {
    this.jobData.model = value;

    if (!value) {
      this.confirmedActualModel = '';
      this.confirmedActualModelResponse = null;
      this.clearMainBoardFirmwareDetails();
      return;
    }

    const previousModel = this.confirmedActualModel;
    this.isSignedMetadataLoading = true;

    this.getSignedMetadataForModel(value).subscribe({
      next: (response: SignedMetadataRecord[]) => {
        this.isSignedMetadataLoading = false;

        if (!response?.length) {
          this.confirmedActualModel = '';
          this.confirmedActualModelResponse = null;
          this.jobData.actual_model = '';
          this.jobData.model = '';
          this.clearMainBoardFirmwareDetails();
          this.toaster.openDialogBox('No Signed Images available for this model', false);
          return;
        }

        // Sort records by signed_at descending (most recent first)
        const sortedResponse = response.slice().sort((a, b) => {
          // Compare as ISO strings (lexicographical, works for ISO 8601)
          if (a.signed_at > b.signed_at) return -1;
          if (a.signed_at < b.signed_at) return 1;
          return 0;
        });

        const dialogRef = this.dialog.open(OtaModelSelectionDialogComponent, {
          width: '80vw',
          maxWidth: '1200px',
          height: '80vh',
          panelClass: 'ota-detail-dialog-panel',
          disableClose: true,
          data: {
            selectedModel: value,
            details: sortedResponse
          }
        });

        dialogRef.afterClosed().subscribe((result?: OtaModelSelectionDialogResult) => {
          if (result?.action === 'accept') {
            this.confirmedActualModel = value;
            this.jobData.actual_model = value;
            this.jobData.model = value;
            this.confirmedActualModelResponse = result.selectedRecord ? [result.selectedRecord] : sortedResponse;

            if (this.isMainBoardFirmwareDisabled) {
              this.clearMainBoardFirmwareDetails();
            }
            return;
          }
          this.confirmedActualModel = '';
          this.confirmedActualModelResponse = null;
          this.jobData.actual_model = '';
          this.jobData.model = '';
          this.clearMainBoardFirmwareDetails();
        });
      },
      error: () => {
        this.isSignedMetadataLoading = false;
        this.confirmedActualModelResponse = null;
        this.jobData.actual_model = '';
        this.jobData.model = '';
        this.clearMainBoardFirmwareDetails();
      }
    });
  }

  private getSignedMetadataForModel(model: string): Observable<SignedMetadataRecord[]> {
    if (this.useMockSignedMetadata) {
      return of(this.getMockSignedMetadata(model)).pipe(delay(300));
    }

    return this.otaService.getSignedMetaDetaForModel(model);
  }

  private buildCreateJobPayload(): JobCreationData {
    const signedMetadata = this.confirmedActualModelResponse?.[0]?.sign_metadata;

    const payload: any = {
      job_name: this.jobData.job_name,
      model: this.jobData.model,
      actual_model: this.jobData.actual_model,
      wm_upgrade: {
        file_name: signedMetadata?.wifi?.file_name || '',
        signature: signedMetadata?.wifi?.signature || '',
        file_size: signedMetadata?.wifi?.file_size || 0,
        firmware_version: this.jobData.wifi_firmware_version || '',
        file_path_on_device: this.jobData.wifi_firmware_path_on_device || '',
        description: this.jobData.wifi_firmware_description || '',
        priority: Number(this.jobData.wifi_firmware_priority || 0)
      },
      job_executions_rollout_config: {
        exponentialRate: {
          baseRatePerMinute: this.jobData.job_executions_rollout_config?.baseRatePerMinute || 50,
          incrementFactor: this.jobData.job_executions_rollout_config?.incrementFactor || 2,
          rateIncreaseCriteria: {
            numberOfSucceededThings: this.jobData.job_executions_rollout_config?.numberOfSucceededThings || 1000
          }
        },
        maximumPerMinute: this.jobData.job_executions_rollout_config?.maximumPerMinute || 1000
      },
      abort_config: {
        criteriaList: (this.jobData.abort_config?.configurations || []).map((item) => ({
          action: item.action,
          failureType: item.failureType,
          minNumberOfExecutedThings: item.minExecutedThings,
          thresholdPercentage: item.thresholdPercentage
        }))
      },
      inprogress_timeout_in_minutes: this.jobData.inprogress_timeout_in_minutes
    };

    if (this.jobData.main_firmware_version) {
      payload.mb_upgrade = {
        file_name: signedMetadata?.mb?.file_name || '',
        signature: signedMetadata?.mb?.signature || '',
        file_size: signedMetadata?.mb?.file_size || 0,
        firmware_version: this.jobData.main_firmware_version || '',
        file_path_on_device: this.jobData.main_firmware_path_on_device || '',
        description: this.jobData.main_firmware_description || '',
        priority: Number(this.jobData.main_firmware_priority || 0)
      };
    }

    return payload;
  }

  private getMockSignedMetadata(model: string): SignedMetadataRecord[] {
    return [
      {
      "model": "EssWFAC",
      "signed_at": "2026-05-18T12:41:31.758503Z",
      "sign_metadata": {
        "mb": {
          "file_path": "s3://remotelync-ota-dev-us-east-1/IRIS_WIFI_FW_01_00_11.bin",
          "signature": "IoYMAyOgCDOPJmOVhB2dgot3p13RK9eZ42EdowW6OC8ArQbK4VYs3v6Fs7ZcPDR0vzGTVKpaR75cu0cxvBDnPSyroadLYXoRtrUzg9PffbWOxL4LQ908nVmPqe/jEWMgGTwpRfzi9kvfhKPJQgXg9Tc1BX5yMaXRemmhpSpeqOW5wu5e9S04AtRJ678ZajqQJlaVwRr18DpPWfmzN/eoqpUjtexPmE3Uwxku6bmeXfZ5bouZY4AgdZ66vXlHAiREJTf4DrTBrj5Y0EIIgLIIQXzr5bJa/RbYOhZvCrVnM0wPsaLU1TEDQdVAf7s4cb1KEAY5mbfEGvIKp7EQj+Xw9XipYB+GpDoZWJL00kcju1+baC+V2HKRw1RwC6QXFLjfCSQuXJAECVydB9DQLbte3uLi1Uslen53rBPTVK/cV0Azk8zV5HNIOA4tfZc9xzO8IE1esxMdWn+NC92Y5DXKxCeyyyXKRUnC20qwfBaEbWewbjihs9J0Al05piz3WUtA",
          "file_size": 1264016,
          "file_name": "IRIS_WIFI_FW_01_00_11.bin"
        },
        "wifi": {
          "file_path": "s3://remotelync-ota-dev-us-east-1/IRIS_WIFI_FW_01_00_11_signed.bin",
          "signature": "RwyTpO/3Dn+4JWEz9mYpRHArOl906nec+TPqCK395uhwv1JrG11FKCo0mQ1Q8WshbNI0hVtfKC3Xoex8vF9Wfk+jR+vi8q8ymcw4r5cgRg1wlBNbeIxau2QHOf4EebXtuF22sTCs+4ONMclUad6gG9jX2eLyRb5OG08yGnX9I5VIpkK8y+Ki8bRnF1b6261JvLTUa+ehwzH3tA4QIi5bdJAfA3OW1NP8XipOmADfhHEUV6ekyMy3ke4pj3z4FidlUqvhb+sNH5Axes00egJBYAUEcDjyD16lC+n3RySRWNv0ouYqVi5fftbXYeEDrTvJy+dBHtcJhax61fSG1Fgw6/8XqZKSSN7xKDopk4ZlReGTOlgnBJIjG0HmOfWBPEm524uY3q1LxM+9hiESDStgdmZSCY7QGIAR16NRa5awAD2n+1K1pwtvKe+34b7REDPufOoGaiROyacsfIkygNF9wmT6lQeORVjQEuiVSbieBSH0BHCGq0tzni1SwUsOdqSh",
          "file_size": 1269760,
          "file_name": "IRIS_WIFI_FW_01_00_11_signed.bin"
        }
      },
      "description": "test-comment - 18th May 2026 at 12:41 PM UTC - This is a mock record used for testing the OTA Job Creation flow. It simulates the response from the backend when fetching signed metadata for a specific model. The actual implementation would fetch real data based on the selected model."
    },
    {
      "model": "EssWFAC",
      "signed_at": "2026-05-19T12:41:31.758503Z",
      "sign_metadata": {
        "mb": {
          "file_path": "s3://remotelync-ota-dev-us-east-1/IRIS_WIFI_FW_01_00_11.bin",
          "signature": "IoYMAyOgCDOPJmOVhB2dgot3p13RK9eZ42EdowW6OC8ArQbK4VYs3v6Fs7ZcPDR0vzGTVKpaR75cu0cxvBDnPSyroadLYXoRtrUzg9PffbWOxL4LQ908nVmPqe/jEWMgGTwpRfzi9kvfhKPJQgXg9Tc1BX5yMaXRemmhpSpeqOW5wu5e9S04AtRJ678ZajqQJlaVwRr18DpPWfmzN/eoqpUjtexPmE3Uwxku6bmeXfZ5bouZY4AgdZ66vXlHAiREJTf4DrTBrj5Y0EIIgLIIQXzr5bJa/RbYOhZvCrVnM0wPsaLU1TEDQdVAf7s4cb1KEAY5mbfEGvIKp7EQj+Xw9XipYB+GpDoZWJL00kcju1+baC+V2HKRw1RwC6QXFLjfCSQuXJAECVydB9DQLbte3uLi1Uslen53rBPTVK/cV0Azk8zV5HNIOA4tfZc9xzO8IE1esxMdWn+NC92Y5DXKxCeyyyXKRUnC20qwfBaEbWewbjihs9J0Al05piz3WUtA",
          "file_size": 1264016,
          "file_name": "IRIS_WIFI_FW_01_00_11.bin"
        },
        "wifi": {
          "file_path": "s3://remotelync-ota-dev-us-east-1/IRIS_WIFI_FW_01_00_11_signed.bin",
          "signature": "RwyTpO/3Dn+4JWEz9mYpRHArOl906nec+TPqCK395uhwv1JrG11FKCo0mQ1Q8WshbNI0hVtfKC3Xoex8vF9Wfk+jR+vi8q8ymcw4r5cgRg1wlBNbeIxau2QHOf4EebXtuF22sTCs+4ONMclUad6gG9jX2eLyRb5OG08yGnX9I5VIpkK8y+Ki8bRnF1b6261JvLTUa+ehwzH3tA4QIi5bdJAfA3OW1NP8XipOmADfhHEUV6ekyMy3ke4pj3z4FidlUqvhb+sNH5Axes00egJBYAUEcDjyD16lC+n3RySRWNv0ouYqVi5fftbXYeEDrTvJy+dBHtcJhax61fSG1Fgw6/8XqZKSSN7xKDopk4ZlReGTOlgnBJIjG0HmOfWBPEm524uY3q1LxM+9hiESDStgdmZSCY7QGIAR16NRa5awAD2n+1K1pwtvKe+34b7REDPufOoGaiROyacsfIkygNF9wmT6lQeORVjQEuiVSbieBSH0BHCGq0tzni1SwUsOdqSh",
          "file_size": 1269760,
          "file_name": "IRIS_WIFI_FW_01_00_11_signed.bin"
        }
      },
      "description": "test-comment - 19th May 2026 at 12:41 PM UTC"
    },
    {
            "model": "EssWFAC",
            "signed_at": "2026-05-12T08:21:28.872076Z",
            "sign_metadata": {
                "mb": {
                    "file_path": "s3://remotelync-ota-dev-us-east-1/IRIS_WIFI_FW_01_00_11.bin",
                    "signature": "V5KEk+nLMVzr3lQIKjHw7vZaNfb60WW/mXR7YwdEBrzOsrh+AcA724Ko5QBE6SL4iVVUhVL74Gj9GVcTVSeiHXFyYyA5JpXO6si/MCS+hUS0De/1yO9kKpGWfLiJYZVbsEk2YYxppTmUmQnWKWdec/IOP3DXjDeTd5yKtgEz5OY4H/BFQUcfkTTfHrsUD0iz7uW7Jb4+SMmj7awG7Dqt5mCVnkSf09DokcybaTE6I0ktVNlBTtS5Lr/clmz0KaEgIray8sACtOuf79ZOlB0rcAKv6vXpM8LRvxNYaecYy1yL5n7I9Ng3qkL7CO9Q/jO5nMNvQmp9B7bqMWrOVv5WZ5uuhueO/erKAphbYD2q7VU0QQovM6ymw3iLCyDdGE5PPTQv8DqUW1bcWofqBITisjgGHH+BJmJEXF7Xoj/oRXgASRe+v/JhJ67rPzEAjAv5MhEKIfyd5+ZBJrpdL3BJh2UxKR2SjTJ2QvBZNFxIekqfWKp8nSyBqyrD6ukxjySA",
                    "file_size": 1264016,
                    "file_name": "IRIS_WIFI_FW_01_00_11.bin"
                },
                "wifi": {
                    "file_path": "s3://remotelync-ota-dev-us-east-1/IRIS_WIFI_FW_01_00_11_signed.bin",
                    "signature": "owOIOIr9qgrCG/O316ijpGCzB0AxUyFGpJtsyytJA6A98HxFgrBC+49YxDAJT24Xis9f5Ba78m0VfnxdiZUKaosd4rJvVHyFD4hsTMF+sWJQnCFVJajNEfJQGhLX4TtSYYiHM5vWV1FJZxAnjLVMiNYrSeNUmLhQ+ksaHvZaNs1+XCEqxnPZwCkctFI4VZvcFtJHCCNKMIsb+75Au/ItLKmetOOG2Jhkcef4/aATaoH6eDJ+ViEacD+Ps4jmKui8AYhxtE+sY9F8cofA3JYklq6VPBBh9gqH8uvN0V697mn8fVh1hWM7c0w9RflW/7cRXip+mVd+6ImDIdefAYRoRQ11Kx3GOeCurH9iNvYFt6g4Ds7eezvvVaCTogHIQ9SB2af2RjQbDnErPIoTLBnasOoCyhshFggF0SBhaJdCzKpjjkvr7XvE2ZkrIeUuG7sI113ou7Gm5go2TJS1Q5xhnYs01F7Y+zqIwNyzn0kZr4RwiEEVUb8Edkb0uq3piZwT",
                    "file_size": 1269760,
                    "file_name": "IRIS_WIFI_FW_01_00_11_signed.bin"
                }
            },
            "description": "test-comment"
        }
  ]

  }
}